import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TOOLTIP_Z } from '../../lib/layers';
import './tooltip.css';

/**
 * How long the pointer or focus has to rest on a trigger before its bubble
 * opens. Set ONCE, here — there is no per-button override, so every icon
 * button in the app answers "what does this do" at the same speed.
 *
 * Native `title` attributes are what this replaces: their delay belongs to the
 * browser (1–4s depending on platform) and cannot be tuned. Pinned by
 * `tooltip.test.tsx`; raising it reddens that suite.
 */
export const TOOLTIP_OPEN_DELAY_MS = 100;

/**
 * After a bubble closes, the next one opens with NO delay if the pointer
 * reaches it within this window — so sweeping along a row of icon buttons
 * reads each label as it is crossed instead of re-waiting per button.
 */
export const TOOLTIP_SKIP_DELAY_MS = 400;

/** Gap between the trigger and the bubble, and the bubble's minimum distance from the viewport edge. */
const GAP_PX = 6;
const EDGE_PX = 8;

/*
 * Shared timing state. Module-level on purpose: the skip window has to span
 * separate Tooltip instances, and one open bubble at a time is the rule.
 */
let lastClosedAt = 0;
let closeOpenBubble: (() => void) | null = null;

interface TooltipProps {
  /** Tooltip text. When empty, the trigger renders on its own. */
  content: React.ReactNode;
  children: React.ReactNode;
  /**
   * Preferred side. Top and bottom flip when there is no room. "right" is for
   * the collapsed sidebar rail: the bubble sits beside the icon, vertically
   * centred, where it covers nothing but the page.
   */
  side?: 'top' | 'bottom' | 'right';
  /** "end" right-aligns the bubble to the trigger rather than centring it. */
  align?: 'center' | 'end';
  /** Classes for the wrapping span (e.g. a width utility when the trigger is full-width). */
  className?: string;
}

/**
 * The app's one hover/focus label for icon buttons.
 *
 * - Opens after TOOLTIP_OPEN_DELAY_MS on mouse or pen hover and on keyboard
 *   focus; instantly when another bubble closed within TOOLTIP_SKIP_DELAY_MS.
 * - Touch never opens it: a tap is a click, and a bubble left over a phone
 *   screen would sit there until the next tap. The bubble is
 *   `pointer-events: none` either way, so it can never swallow a tap.
 * - Closes on pointer leave, blur, click, Escape and scroll.
 * - Portalled to `document.body` with fixed positioning, so an
 *   `overflow-hidden` panel or an `overflow-x-auto` table cannot clip it.
 *
 * The bubble is `aria-hidden` — it is decorative. Every icon-only trigger
 * carries its own accessible name in `aria-label`, normally the same words.
 * Never also set `title` on the trigger: the native tooltip would draw on top
 * of this one, late.
 *
 * The trigger is wrapped in a span so a DISABLED button still shows why it is
 * disabled — a disabled button receives no pointer events of its own. A
 * disabled button cannot take focus either, so when the child is disabled the
 * wrapper itself joins the tab order: a keyboard user tabs to it and reads the
 * reason, where a native `title` gave them nothing at all.
 *
 * String content also becomes the trigger's `aria-description`, so a screen
 * reader still hears what a native `title` used to announce — unless the
 * trigger's `aria-label` already says the same words (the usual icon button),
 * which would say them twice.
 */
export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  side = 'top',
  align = 'center',
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // A pointer press focuses the button too; that focus must not open the bubble.
  const pressed = useRef(false);
  // After a click the bubble stays shut until the pointer leaves and comes back.
  const suppressed = useRef(false);

  const hide = useCallback(() => {
    clearTimeout(timer.current);
    setOpen((was) => {
      if (was) lastClosedAt = Date.now();
      return false;
    });
    setPos(null);
  }, []);

  const show = useCallback(() => {
    if (!content || suppressed.current) return;
    clearTimeout(timer.current);
    const instant = closeOpenBubble !== null || Date.now() - lastClosedAt < TOOLTIP_SKIP_DELAY_MS;
    const doOpen = () => {
      if (closeOpenBubble && closeOpenBubble !== hide) closeOpenBubble();
      closeOpenBubble = hide;
      setOpen(true);
    };
    if (instant) doOpen();
    else timer.current = setTimeout(doOpen, TOOLTIP_OPEN_DELAY_MS);
  }, [content, hide]);

  useEffect(
    () => () => {
      clearTimeout(timer.current);
      if (closeOpenBubble === hide) closeOpenBubble = null;
    },
    [hide],
  );

  useEffect(() => {
    if (!open) {
      if (closeOpenBubble === hide) closeOpenBubble = null;
      return;
    }
    const onScroll = () => hide();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, hide]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !bubbleRef.current) return;
    const t = triggerRef.current.getBoundingClientRect();
    const b = bubbleRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (side === 'right') {
      const top = Math.max(EDGE_PX, Math.min(t.top + t.height / 2 - b.height / 2, vh - b.height - EDGE_PX));
      setPos({ top, left: Math.min(t.right + GAP_PX, vw - b.width - EDGE_PX) });
      return;
    }
    let top =
      side === 'top' ? t.top - b.height - GAP_PX : t.bottom + GAP_PX;
    if (side === 'top' && top < EDGE_PX) top = t.bottom + GAP_PX;
    if (side === 'bottom' && top + b.height > vh - EDGE_PX) top = t.top - b.height - GAP_PX;
    let left = align === 'end' ? t.right - b.width : t.left + t.width / 2 - b.width / 2;
    left = Math.max(EDGE_PX, Math.min(left, vw - b.width - EDGE_PX));
    setPos({ top, left });
  }, [open, side, align, content]);

  const onPointerEnter = (e: React.PointerEvent) => {
    if (e.pointerType === 'touch') return;
    show();
  };
  const onPointerLeave = () => {
    suppressed.current = false;
    hide();
  };
  const onPointerDown = () => {
    pressed.current = true;
    suppressed.current = true;
    hide();
  };
  const onFocus = () => {
    if (pressed.current) return;
    show();
  };
  const onBlur = () => {
    pressed.current = false;
    hide();
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && open) hide();
  };

  const child = React.Children.count(children) === 1 && React.isValidElement(children)
    ? (children as React.ReactElement<Record<string, unknown>>)
    : null;
  const childDisabled = !!child?.props.disabled;
  // Described unless the trigger's own name already says the same words. An
  // attribute rather than a hidden node, so the words do not appear twice in
  // the page's text.
  const label = child?.props['aria-label'];
  const describe = typeof content === 'string' && !!content && !!child
    && !child.props['aria-labelledby'] && !child.props['aria-description'] && label !== content;
  const trigger = describe ? React.cloneElement(child!, { 'aria-description': content }) : children;

  return (
    <span
      ref={triggerRef}
      tabIndex={childDisabled && content ? 0 : undefined}
      className={`inline-flex ${childDisabled && content ? 'rounded-fi-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fi-amber' : ''} ${className}`}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onPointerDown={onPointerDown}
      onPointerUp={() => { pressed.current = false; }}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    >
      {trigger}
      {open && !!content &&
        createPortal(
          <span
            ref={bubbleRef}
            data-testid="tooltip"
            aria-hidden="true"
            className="fi-tooltip"
            style={{
              zIndex: TOOLTIP_Z,
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              visibility: pos ? 'visible' : 'hidden',
            }}
          >
            {content}
          </span>,
          document.body,
        )}
    </span>
  );
};

export default Tooltip;
